import { Component, Input, OnDestroy } from '@angular/core';
import { NbDialogRef, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import {
  DozentService,
  ExerciseParseJobStatus,
} from '../../../services/dozent.service';

const MAX_FILES = 20;
const MAX_FILE_MB = 20;
const POLL_INTERVAL_MS = 2500;

/**
 * Result contract on close:
 *  - { openExerciseId }  single file finished successfully → open the editor
 *  - { pendingBatchId }  still processing → page keeps polling in background
 *  - { imported }        batch finished while dialog was open
 *  - undefined           nothing started / nothing imported
 *
 * The import itself is a pure background process: the worker creates the
 * draft exercises server-side (result.exercise_id). Closing this dialog
 * never cancels or loses anything.
 */
@Component({
  selector: 'app-exercise-import-dialog',
  templateUrl: './exercise-import-dialog.component.html',
  styleUrl: './exercise-import-dialog.component.scss',
})
export class ExerciseImportDialogComponent implements OnDestroy {
  /** Called whenever a job finished so the page can refresh the list. */
  @Input() onImported: () => void = () => {};

  phase: 'select' | 'processing' | 'summary' = 'select';
  selectedFiles: File[] = [];
  fileError = '';
  dragOver = false;

  batchId = '';
  jobs: ExerciseParseJobStatus[] = [];
  quotaExhausted = false;
  uploadError = '';

  private pollTimer?: ReturnType<typeof setInterval>;
  private toastedJobIds = new Set<string>();

  constructor(
    protected dialogRef: NbDialogRef<ExerciseImportDialogComponent>,
    private dozentService: DozentService,
    private toastrService: NbToastrService,
    private t: TranslateService
  ) {}

  ngOnDestroy(): void {
    this.stopPolling();
  }

  // ── File selection ──

  onFilesSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    this.addFiles(Array.from(input.files || []));
    input.value = '';
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.dragOver = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.dragOver = false;
  }

  onFilesDropped(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.dragOver = false;
    if (this.phase !== 'select') return;
    this.addFiles(Array.from(event.dataTransfer?.files || []));
  }

  private addFiles(files: File[]) {
    this.fileError = '';
    for (const file of files) {
      const name = file.name.toLowerCase();
      if (
        !name.endsWith('.pdf') &&
        !name.endsWith('.txt') &&
        !name.endsWith('.docx') &&
        !name.endsWith('.csv')
      ) {
        this.fileError = this.t.instant(
          'pages.dozent.exerciseImport.errors.unsupportedType',
          { filename: file.name }
        );
        continue;
      }
      if (file.size > MAX_FILE_MB * 1024 * 1024) {
        this.fileError = this.t.instant(
          'pages.dozent.exerciseImport.errors.tooLarge',
          { filename: file.name, max: MAX_FILE_MB }
        );
        continue;
      }
      if (this.selectedFiles.length >= MAX_FILES) {
        this.fileError = this.t.instant(
          'pages.dozent.exerciseImport.errors.tooMany',
          { max: MAX_FILES }
        );
        break;
      }
      this.selectedFiles.push(file);
    }
  }

  removeFile(index: number) {
    this.selectedFiles.splice(index, 1);
  }

  // ── Upload + polling (display only — work happens server-side) ──

  startImport() {
    if (!this.selectedFiles.length) return;
    this.phase = 'processing';
    this.uploadError = '';
    this.quotaExhausted = false;
    this.dozentService.parseExerciseDocuments(this.selectedFiles).subscribe({
      next: (batch) => {
        this.batchId = batch.batch_id;
        this.jobs = batch.jobs.map((job) => ({
          ...job,
          status: 'queued' as const,
          result: null,
          warnings: [],
          error: null,
        }));
        this.pollTimer = setInterval(() => this.poll(), POLL_INTERVAL_MS);
      },
      error: (err) => {
        this.phase = 'select';
        if (err?.status === 429) {
          this.quotaExhausted = true;
          return;
        }
        this.uploadError =
          err?.error?.detail?.message ||
          (typeof err?.error?.detail === 'string' ? err.error.detail : '') ||
          this.t.instant('toasts.error');
      },
    });
  }

  private poll() {
    this.dozentService.getExerciseParseBatch(this.batchId).subscribe({
      next: (status) => {
        this.jobs = status.jobs;
        let anyNewDone = false;
        for (const job of this.jobs) {
          if (job.status === 'done' && !this.toastedJobIds.has(job.job_id)) {
            this.toastedJobIds.add(job.job_id);
            anyNewDone = true;
            const count = (job.result as any)?.count ?? 1;
            this.toastrService.success(
              count > 1
                ? this.t.instant(
                    'pages.dozent.exerciseImport.importedCountToast',
                    { count, filename: job.filename }
                  )
                : this.t.instant('pages.dozent.exerciseImport.importedToast', {
                    title: (job.result as any)?.title || job.filename,
                  }),
              ''
            );
          }
        }
        if (anyNewDone) this.onImported();
        this.finishIfDone();
      },
      error: () => {
        // transient poll failure — background job is unaffected, keep trying
      },
    });
  }

  private finishIfDone() {
    const allSettled = this.jobs.every(
      (job) => job.status === 'done' || job.status === 'error'
    );
    if (!allSettled) return;
    this.stopPolling();
    if (this.phase !== 'processing') return;
    this.phase = 'summary';
    // Single successful file: jump straight into the editor for review.
    const openId =
      this.jobs.length === 1 ? this.exerciseId(this.jobs[0]) : undefined;
    if (openId) {
      this.dialogRef.close({ openExerciseId: openId });
    }
  }

  private stopPolling() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = undefined;
    }
  }

  // ── Template helpers ──

  exerciseId(job: ExerciseParseJobStatus): string | undefined {
    return (job.result as any)?.exercise_id;
  }

  get successCount(): number {
    return this.jobs.filter((job) => job.status === 'done').length;
  }

  /** Number of exercises actually created — a single CSV job can produce N. */
  get createdCount(): number {
    return this.jobs
      .filter((job) => job.status === 'done')
      .reduce((sum, job) => sum + ((job.result as any)?.count ?? 1), 0);
  }

  get errorCount(): number {
    return this.jobs.filter((job) => job.status === 'error').length;
  }

  get processing(): boolean {
    return this.phase === 'processing';
  }

  errorMessage(job: ExerciseParseJobStatus): string {
    return (
      job.error?.message ||
      this.t.instant('pages.dozent.exerciseImport.errors.generic')
    );
  }

  close() {
    this.stopPolling();
    if (this.phase === 'processing' && this.batchId) {
      // Work continues server-side; the page takes over polling.
      this.toastrService.info(
        this.t.instant('pages.dozent.exerciseImport.backgroundToast'),
        ''
      );
      this.dialogRef.close({ pendingBatchId: this.batchId });
      return;
    }
    this.dialogRef.close(
      this.successCount ? { imported: this.successCount } : undefined
    );
  }
}
